import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import Pagination from "../../components/Panigation";
import { useLazyGetTransactionListQuery } from "../../services/transactions";
import { showError } from "../../helpers";
import type { TransactionItem } from "../../types/Transaction";
import { isValidInput } from "../../utils/validations";

const AllTransactions: React.FC = () => {
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [list, setList] = useState<TransactionItem[]>([]);

  const [getTransactionsData] = useLazyGetTransactionListQuery();

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || 0) / (pageSize || 10))
  );

  const fetchList = useCallback(async () => {
    try {
      const params: any = {
        page: page,
        limit: pageSize,
        search: debouncedSearchTerm,
      };
      const response = await getTransactionsData(params).unwrap();
      if (response?.statusCode === 200) {
        const data = response?.data || { transactionData: [], count: 0 };
        setList(data?.transactionData || []);
        setTotalCount(data?.count || 0);
      }
    } catch (error: unknown) {
      const apiMessage = (error as { data?: { message?: string } })?.data
        ?.message;
      showError(apiMessage || "Failed to fetch Transactions");
    }
  }, [page, pageSize, debouncedSearchTerm, getTransactionsData]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  return (
    <>
      <div className="table_header">
        <div className="left_s">
          <SearchBar
            searchTerm={searchTerm}
            placeholder="Search..."
            setDebouncedSearchTerm={setDebouncedSearchTerm}
            value={searchTerm}
            onCross={() => setSearchTerm("")}
            onChange={(val: any) => {
              if (isValidInput(val.target.value)) {
                setSearchTerm(val.target.value);
              }
            }}
          />
        </div>
      </div>

      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Receipt ID</TableCell>
              <TableCell>Transaction Date</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Points Redeemable From</TableCell>
              <TableCell>Purchase Date</TableCell>
              <TableCell>Expire Date</TableCell>
              <TableCell>Entry Type</TableCell>
              <TableCell>Points</TableCell>
              <TableCell>Source Type</TableCell>
              <TableCell>Post Terminal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(list || []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No data found
                </TableCell>
              </TableRow>
            ) : (
              (list || []).map((tx: TransactionItem) => {
                const value =
                  tx?.entryType === "Expire"
                    ? tx?.expiredPoints || 0
                    : tx?.points || 0;
                const isPositive = tx?.entryType === "Sales";
                const signed = isPositive ? value : -value;
                const user = tx?.userDetails || ({} as any);
                return (
                  <TableRow key={tx?._id}>
                    <TableCell>{tx?.receiptNo || "-"}</TableCell>
                    <TableCell>
                      {tx?.createdAt
                        ? new Date(tx.createdAt).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Box
                        component="a"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/manage-users/detail/" + tx?.userId);
                        }}
                        className="user_block click_action"
                      >
                        <p>
                          <b>{user?.fullName || "-"}</b>
                          <span>
                            {`${user?.dialCode || ""} ${
                              user?.phoneNo || ""
                            }`.trim()}
                          </span>
                          <span>{user?.email || ""}</span>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.location || "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.pointsRedeemableFrom
                        ? new Date(tx.pointsRedeemableFrom).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.purchaseDate
                        ? new Date(tx.purchaseDate).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.expires && tx.expires[0]
                        ? new Date(tx.expires[0]).toLocaleString()
                        : "-"}
                    </TableCell>
                    <TableCell>{tx?.entryType || "-"}</TableCell>
                    <TableCell>
                      <p
                        className={`status_btn ${
                          signed > 0 ? "c_success" : "c_danger"
                        }`}
                      >{`${signed > 0 ? "+" : ""}${signed} pts`}</p>
                    </TableCell>
                    <TableCell>{tx?.sourceType || "-"}</TableCell>
                    <TableCell style={{ textAlign: "center" }}>
                      {tx?.posTerminal || "-"}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        module={list || []}
        page={page}
        setPage={setPage}
        limit={pageSize}
        setLimit={setPageSize}
        onPageChange={onPageChange}
        totalPages={totalPages}
      />
    </>
  );
};

export default AllTransactions;
